// src/components/AIAgentsList.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaPlay, FaTools, FaEnvelope, FaCheck, FaTimes, FaWhatsapp, FaFacebook, FaTwitter, FaSlack, FaInstagram, FaDiscord, FaTelegram, FaAmazon, FaComments, FaCode, FaPlus, FaTag } from 'react-icons/fa';
import './AIAssistant.css';
import { useNavigate } from 'react-router-dom';
import Button from './UI/Button';

const AIAgentsList = ({ onSelectAgent }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [aiAssistants, setAIAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState([]);
  const [userEmails, setUserEmails] = useState({
    gmail_email: null,
    outlook_emails: []
  });
  const [availableChannels, setAvailableChannels] = useState({});
  const [showEmailSelector, setShowEmailSelector] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [selectedAssistantForEmail, setSelectedAssistantForEmail] = useState(null);
  const [selectedAssistantForChannels, setSelectedAssistantForChannels] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState({});
  const [associationLoading, setAssociationLoading] = useState(false);
  const [associationSuccess, setAssociationSuccess] = useState(null);
  const [channelAssociationLoading, setChannelAssociationLoading] = useState(false);
  const [channelAssociationSuccess, setChannelAssociationSuccess] = useState(null);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentPrompt, setNewAgentPrompt] = useState('');
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  
  // Brand association state
  const [availableBrands, setAvailableBrands] = useState([]);
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const [selectedAssistantForBrand, setSelectedAssistantForBrand] = useState(null);
  const [brandAssociationLoading, setBrandAssociationLoading] = useState(false);
  const [brandAssociationSuccess, setBrandAssociationSuccess] = useState(null);

  // Dummy Data for "experience" role
  const dummyAIAssistants = [
    { _id: 'exp_agent_1', name: 'Support Pro Agent (Experience)', prompt: 'You are a helpful support agent. Prioritize clarity and quick resolutions.', selected_tool_ids: ['tool_exp_1', 'tool_exp_2'], associated_email: 'experience_user@gmail.com', associated_channels: { 'whatsapp': true, 'website': false } },
    { _id: 'exp_agent_2', name: 'Sales Assistant Bot (Experience)', prompt: 'Your goal is to guide users to the best products and assist with sales inquiries.', selected_tool_ids: ['tool_exp_3'], associated_channels: { 'messenger': true } },
    { _id: 'exp_agent_3', name: 'Tech Troubleshooter (Experience)', prompt: 'Help users with technical issues and guide them through troubleshooting steps.', selected_tool_ids: [], associated_email: 'experience_user_outlook@example.com' },
  ];
  const dummyAvailableTools = [
    { id: 'tool_exp_1', name: 'retrieve_knowledge' },
    { id: 'tool_exp_2', name: 'resolve_ticket' },
    { id: 'tool_exp_3', name: 'getOrderById' },
  ];
  const dummyUserEmails = {
    gmail_email: 'experience_user@gmail.com',
    outlook_emails: ['experience_user_outlook@example.com']
  };
  const dummyAvailableChannels = {
    'whatsapp': true,
    'messenger': true,
    'website': true,
    'instagram': false,
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      loadInitialData(token);
    } else {
      navigate('/');
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response && (error.response.status === 401 || error.response.status === 422)) {
        localStorage.removeItem('access_token');
        navigate('/');
      }
      return null;
    }
  };

  const loadInitialData = async (token) => {
    setIsLoading(true);
    const fetchedUser = await fetchUserData(token);
    setUser(fetchedUser);

    if (fetchedUser && fetchedUser.role === 'experience') {
      setAIAssistants(dummyAIAssistants);
      setAvailableTools(dummyAvailableTools);
      setUserEmails(dummyUserEmails);
      setAvailableChannels(dummyAvailableChannels);
      setIsLoading(false);
    } else if (fetchedUser) {
      await Promise.all([
        fetchAssistantsAndTools(token),
        fetchUserEmailsData(token),
        fetchAvailableChannelsData(token),
        fetchAvailableBrandsData(token)
      ]);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    const createConnectionLines = () => {
      document.querySelectorAll('.connection-line').forEach(line => line.remove());
      
      // Create email connections
      document.querySelectorAll('.agent-card[data-email]').forEach(agentEl => {
        const associatedEmail = agentEl.getAttribute('data-email');
        if (!associatedEmail) return;
        
        const emailCircle = document.querySelector(`.email-circle[data-email="${associatedEmail}"]`);
        if (!emailCircle) return;
        
        createConnectionLine(agentEl, emailCircle, 'var(--brand-accent)');
      });
      
      // Create channel connections
      document.querySelectorAll('.agent-card').forEach(agentEl => {
        const assistantId = agentEl.getAttribute('data-assistant-id');
        if (!assistantId) return;
        
        const assistant = aiAssistants.find(a => a._id === assistantId);
        if (!assistant || !assistant.associated_channels) return;
        
        Object.entries(assistant.associated_channels).forEach(([channelName, isActive]) => {
          if (!isActive) return;
          
          const channelCircle = document.querySelector(`.ai-channel-circle[data-channel="${channelName}"]`);
          if (!channelCircle) return;
          
          createConnectionLine(agentEl, channelCircle, 'var(--state-warning)');
        });
      });
      
      // Create brand connections
      document.querySelectorAll('.agent-card').forEach(agentEl => {
        const assistantId = agentEl.getAttribute('data-assistant-id');
        if (!assistantId) return;
        
        const assistant = aiAssistants.find(a => a._id === assistantId);
        if (!assistant || !assistant.associated_brand_id) return;
        
        const brandCircle = document.querySelector(`.brand-circle[data-brand="${assistant.associated_brand_id}"]`);
        if (!brandCircle) return;
        
        createConnectionLine(agentEl, brandCircle, 'var(--state-info)');
      });
    };
    
    const createConnectionLine = (sourceEl, targetEl, color) => {
      const sourceRect = sourceEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      
      if (sourceRect.width === 0 || sourceRect.height === 0 || targetRect.width === 0 || targetRect.height === 0) {
        return; 
      }

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      line.classList.add('connection-line');
      line.style.position = 'absolute';
      
      const bodyRect = document.body.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calculate dimensions for the SVG container
      const minX = Math.min(sourceRect.left, targetRect.left);
      const maxX = Math.max(sourceRect.right, targetRect.right);
      const minY = Math.min(sourceRect.bottom, targetRect.top);
      const maxY = Math.max(sourceRect.bottom, targetRect.bottom);
      
      line.style.top = `${minY + scrollTop}px`;
      line.style.left = `${minX}px`;
      line.style.width = `${maxX - minX}px`;
      line.style.height = `${maxY - minY}px`;
      line.style.pointerEvents = 'none';
      line.style.zIndex = '0'; // Behind cards
      
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      
      // Start from bottom center of source card
      const startX = (sourceRect.left + sourceRect.width / 2) - minX;
      const startY = sourceRect.bottom - (minY + scrollTop);
      
      // End at top center of target circle
      const endX = (targetRect.left + targetRect.width / 2) - minX;
      const endY = targetRect.top - (minY + scrollTop);
      
      // Increase control point offset to curve around cards
      const controlPointOffsetY = Math.max(80, Math.abs(endY - startY) * 0.6);
      
      // Create smooth curve that goes around cards
      const pathData = `M${startX},${startY} C${startX},${startY + controlPointOffsetY} ${endX},${endY - controlPointOffsetY} ${endX},${endY}`;
      
      path.setAttribute('d', pathData);
      path.setAttribute('stroke', color);
      path.setAttribute('stroke-width', '2');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-dasharray', '4,4');
      path.classList.add('pulsating-path');
      
      line.appendChild(path);
      document.body.appendChild(line);
    };
    
    if (aiAssistants.length > 0 && !isLoading) {
      const timeoutId = setTimeout(createConnectionLines, 100);
      window.addEventListener('resize', createConnectionLines);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', createConnectionLines);
        document.querySelectorAll('.connection-line').forEach(line => line.remove());
      };
    }
  }, [aiAssistants, isLoading, userEmails, availableChannels, availableBrands]);

  const fetchAssistantsAndTools = async (token) => {
    try {
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get('/api/tools', {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      setAIAssistants(assistantsResponse.data);
      setAvailableTools(toolsResponse.data);
    } catch (error) {
      console.error('Failed to fetch assistants and tools:', error);
       if (error.response && (error.response.status === 401 || error.response.status === 422)) {
        localStorage.removeItem('access_token');
        navigate('/');
      }
    }
  };

  const fetchUserEmailsData = async (token) => {
    try {
      const response = await axios.get('/api/users/user/emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserEmails(response.data);
    } catch (error) {
      console.error('Failed to fetch user emails:', error);
    }
  };

  const fetchAvailableChannelsData = async (token) => {
    try {
      const response = await axios.get('/api/users/available-channels', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableChannels(response.data.channels || {});
    } catch (error) {
      console.error('Failed to fetch available channels:', error);
    }
  };

  const fetchAvailableBrandsData = async (token) => {
    try {
      const response = await axios.get('/api/brands/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableBrands(response.data);
    } catch (error) {
      console.error('Failed to fetch available brands:', error);
    }
  };

  const getPromptPreview = (text) => {
    const maxLength = 80;
    return text && text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getToolTranslation = (toolName) => {
    const toolTranslations = {
      'retrieve_knowledge': t('tools.retrieveKnowledge'),
      'assign_to_employee': t('tools.assignToEmployee'),
      'resolve_ticket': t('tools.resolveTicket'),
      'Close_ticket': t('tools.closeTicket'),
      'get_tracking_info': t('tools.getTrackingInfo'),
      'Retrieve_customer_order_by_reference_and_email': t('tools.retrieveOrder'),
      'Update_customer_shipping_address': t('tools.updateShippingAddress'),
      'find_relay_points': t('tools.findRelayPoints'),
      'create_return_with_edn_label': t('tools.createReturn'),
      'carriers_conversation': t('tools.carriersConversation'),
      'Retour': t('tools.return'),
      'fill_form_fields': t('tools.fillPDF'),
      'getOrderById': t('tools.getOrderById'),
      'carriers_conversation_update': t('tools.carriersConversationUpdate'),
      'order_status': t('tools.orderStatus'),
      'Request_return_order': t('tools.Request_return_order'),
      'Retrieve_customer_order_by_ID': t('tools.Retrieve_customer_order_by_ID'),
      'Request_exchange_shopify': t('tools.Request_exchange_shopify'),
      'Get_all_product_variants': t('tools.Get_all_product_variants')
    };
    return toolTranslations[toolName] || toolName;
  };

  const handleAssociateEmailClick = (assistantId) => {
    setSelectedAssistantForEmail(assistantId);
    setShowEmailSelector(true);
  };

  const associateEmailWithAssistant = async (email) => {
    if (!selectedAssistantForEmail) return;
    
    setAssociationLoading(true);
    setAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === selectedAssistantForEmail) {
            return { ...assistant, associated_email: email };
          }
          return assistant;
        }));
        setAssociationSuccess(true);
        setAssociationLoading(false);
        setTimeout(() => {
          setShowEmailSelector(false);
          setSelectedAssistantForEmail(null);
          setAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-email',
        {
          email: email,
          assistant_id: selectedAssistantForEmail
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForEmail) {
          return { ...assistant, associated_email: email };
        }
        return assistant;
      }));
      
      setAssociationSuccess(true);
      
      setTimeout(() => {
        setShowEmailSelector(false);
        setSelectedAssistantForEmail(null);
        setAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate email with assistant:', error);
      setAssociationSuccess(false);
      
      setTimeout(() => {
        setAssociationSuccess(null);
      }, 3000);
    } finally {
      setAssociationLoading(false);
    }
  };

  const removeEmailAssociation = async (assistantId, emailToRemove) => {
    setAssociationLoading(true);
    setAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === assistantId) {
            const updatedAssistant = { ...assistant };
            delete updatedAssistant.associated_email;
            return updatedAssistant;
          }
          return assistant;
        }));
        setAssociationSuccess(true);
        setAssociationLoading(false);
        setTimeout(() => {
          setShowEmailSelector(false);
          setSelectedAssistantForEmail(null);
          setAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/remove-email-association',
        {
          email: emailToRemove,
          assistant_id: assistantId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === assistantId) {
          const updatedAssistant = { ...assistant };
          delete updatedAssistant.associated_email;
          return updatedAssistant;
        }
        return assistant;
      }));
      
      setAssociationSuccess(true);
      
      setTimeout(() => {
        setShowEmailSelector(false);
        setSelectedAssistantForEmail(null);
        setAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to remove email association:', error);
      setAssociationSuccess(false);
      
      setTimeout(() => {
        setAssociationSuccess(null);
      }, 3000);
    } finally {
      setAssociationLoading(false);
    }
  };

  const handleAssociateChannelsClick = (assistant) => {
    setSelectedAssistantForChannels(assistant._id);
    const currentAssistant = aiAssistants.find(a => a._id === assistant._id);
    setSelectedChannels(currentAssistant?.associated_channels || {});
    setShowChannelSelector(true);
  };

  const associateChannelsWithAssistant = async () => {
    if (!selectedAssistantForChannels) return;
    
    setChannelAssociationLoading(true);
    setChannelAssociationSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        setAIAssistants(prevAssistants => prevAssistants.map(assistant => {
          if (assistant._id === selectedAssistantForChannels) {
            return { ...assistant, associated_channels: selectedChannels };
          }
          return assistant;
        }));
        setChannelAssociationSuccess(true);
        setChannelAssociationLoading(false);
        setTimeout(() => {
          setShowChannelSelector(false);
          setSelectedAssistantForChannels(null);
          setChannelAssociationSuccess(null);
        }, 1500);
      }, 500);
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-channels',
        {
          assistant_id: selectedAssistantForChannels,
          channels: selectedChannels
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForChannels) {
          return { ...assistant, associated_channels: selectedChannels };
        }
        return assistant;
      }));
      
      setChannelAssociationSuccess(true);
      
      setTimeout(() => {
        setShowChannelSelector(false);
        setSelectedAssistantForChannels(null);
        setChannelAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate channels with assistant:', error);
      setChannelAssociationSuccess(false);
      
      setTimeout(() => {
        setChannelAssociationSuccess(null);
      }, 3000);
    } finally {
      setChannelAssociationLoading(false);
    }
  };

  const handleChannelToggle = (channelName) => {
    setSelectedChannels(prev => ({
      ...prev,
      [channelName]: !prev[channelName]
    }));
  };

  const handleAgentClick = (assistant) => {
    navigate(`/ai-assistants?assistantId=${assistant._id}`);
  };

  const getChannelIcon = (channelName) => {
    const channelIcons = {
      'whatsapp': <FaWhatsapp />,
      'facebook': <FaFacebook />,
      'messenger': <FaFacebook />,
      'twitter': <FaTwitter />,
      'slack': <FaSlack />,
      'instagram': <FaInstagram />,
      'discord': <FaDiscord />,
      'telegram': <FaTelegram />,
      'amazon': <FaAmazon />,
      'website': <FaComments />,
      'custom': <FaTools />
    };
    
    return channelIcons[channelName.toLowerCase()] || <FaComments />;
  };

  const handleCreateAIAssistant = async () => {
    if (!newAgentName.trim()) {
      alert(t('aiAssistant.nameRequired'));
      return;
    }

    setCreateLoading(true);
    setCreateSuccess(null);

    if (user && user.role === 'experience') {
      setTimeout(() => {
        const newDummyAssistant = {
          _id: `exp_asst_${Date.now()}`,
          name: newAgentName,
          prompt: newAgentPrompt || 'Default demo prompt.',
          selected_tool_ids: selectedToolIds || [],
          is_running: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setAIAssistants(prev => [...prev, newDummyAssistant]);
        setCreateSuccess(true);
        setCreateLoading(false);
        
        setTimeout(() => {
          resetCreateForm();
          setShowCreateAgentModal(false);
          setCreateSuccess(null);
        }, 1500);
      }, 500);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants',
        { 
          name: newAgentName, 
          prompt: newAgentPrompt, 
          selected_tool_ids: selectedToolIds 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAIAssistants(prev => [...prev, response.data]);
      setCreateSuccess(true);
      
      setTimeout(() => {
        resetCreateForm();
        setShowCreateAgentModal(false);
        setCreateSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to create AI assistant:', error);
      setCreateSuccess(false);
      
      setTimeout(() => {
        setCreateSuccess(null);
      }, 3000);
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewAgentName('');
    setNewAgentPrompt('');
    setSelectedToolIds([]);
  };

  const handleAssociateBrandClick = (assistantId) => {
    setSelectedAssistantForBrand(assistantId);
    setShowBrandSelector(true);
  };

  const associateBrandWithAssistant = async (brandId) => {
    if (!selectedAssistantForBrand) return;
    
    setBrandAssociationLoading(true);
    setBrandAssociationSuccess(null);
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/associate-brand',
        {
          brand_id: brandId,
          assistant_id: selectedAssistantForBrand
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === selectedAssistantForBrand) {
          return { ...assistant, associated_brand_id: brandId };
        }
        return assistant;
      }));
      
      setBrandAssociationSuccess(true);
      
      setTimeout(() => {
        setShowBrandSelector(false);
        setSelectedAssistantForBrand(null);
        setBrandAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to associate brand with assistant:', error);
      setBrandAssociationSuccess(false);
      
      setTimeout(() => {
        setBrandAssociationSuccess(null);
      }, 3000);
    } finally {
      setBrandAssociationLoading(false);
    }
  };

  const removeBrandAssociation = async (assistantId, brandId) => {
    setBrandAssociationLoading(true);
    setBrandAssociationSuccess(null);
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/ai-assistants/remove-brand-association',
        {
          brand_id: brandId,
          assistant_id: assistantId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setAIAssistants(aiAssistants.map(assistant => {
        if (assistant._id === assistantId) {
          const updatedAssistant = { ...assistant };
          delete updatedAssistant.associated_brand_id;
          return updatedAssistant;
        }
        return assistant;
      }));
      
      setBrandAssociationSuccess(true);
      
      setTimeout(() => {
        setShowBrandSelector(false);
        setSelectedAssistantForBrand(null);
        setBrandAssociationSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to remove brand association:', error);
      setBrandAssociationSuccess(false);
      
      setTimeout(() => {
        setBrandAssociationSuccess(null);
      }, 3000);
    } finally {
      setBrandAssociationLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="una-dashboard-loading-screen">
        <div className="una-loading-content">
          <div className="una-loading-logo">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
          </div>
          <h2>{t('aiAssistant.loading')}</h2>
          <div className="una-loading-progress">
            <div className="una-progress-bar">
              <div className="una-progress-glow"></div>
            </div>
          </div>
          <div className="una-loading-metrics">
            <div className="una-metric-item">
              <FaRobot />
              <span>{t('aiAssistant.fetchingAssistants')}</span>
            </div>
            <div className="una-metric-item">
              <FaTools />
              <span>{t('aiAssistant.loadingTools')}</span>
            </div>
            <div className="una-metric-item">
              <FaCode />
              <span>{t('aiAssistant.processingConfigurations')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const validEmails = [
    userEmails.gmail_email, 
    ...(userEmails.outlook_emails || [])
  ].filter(email => email);

  // Filter out test channel
  const filteredChannels = Object.keys(availableChannels).filter(channel => channel !== 'test');

  return (
    <div className="agents-list-wrapper">
      <div className="agents-list-header">
        <h3>{t('aiAssistant.availableAgents')}</h3>
        <Button
          variant="primary"
          onClick={() => setShowCreateAgentModal(true)}
          className="create-agent-button"
        >
          <FaPlus /> {t('aiAssistant.createNew')}
        </Button>
      </div>
      
      <div className="agent-cards-grid">
        {aiAssistants.map((assistant) => {
          const assistantTools = availableTools.filter(tool => 
            assistant.selected_tool_ids && assistant.selected_tool_ids.includes(tool.id)
          );
          
          return (
            <div
              key={assistant._id}
              className={`agent-card ${assistant.associated_email ? 'has-email-connection' : ''} ${assistant.associated_channels && Object.values(assistant.associated_channels).some(Boolean) ? 'has-channel-connection' : ''}`}
              onClick={() => handleAgentClick(assistant)}
              data-email={assistant.associated_email}
              data-assistant-id={assistant._id}
            >
              <div className="agent-card-header">
                <div className="agent-circle small">
                  <FaRobot />
                </div>
                <div className="agent-card-title">
                  <h4>{assistant.name}</h4>
                </div>
              </div>
              
              {assistant.prompt && (
                <div className="agent-card-prompt">
                  <h5>{t('aiAssistant.prompt')}:</h5>
                  <p>{getPromptPreview(assistant.prompt)}</p>
                </div>
              )}
              
              {assistantTools.length > 0 && (
                <div className="agent-card-tools">
                  <h5><FaTools /> {t('aiAssistant.tools')}</h5>
                  <div className="agent-tools-list">
                    {assistantTools.slice(0, 3).map(tool => (
                      <span key={tool.id} className="agent-tool-badge">
                        {getToolTranslation(tool.name)}
                      </span>
                    ))}
                    {assistantTools.length > 3 && (
                      <span className="agent-tool-badge more">
                        +{assistantTools.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="agent-card-actions">
                {validEmails.length > 0 && (
                  <Button
                    variant="tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateEmailClick(assistant._id);
                    }}
                  >
                    <FaEnvelope /> {assistant.associated_email ? t('aiAssistant.changeEmail') : t('aiAssistant.associateEmail')}
                  </Button>
                )}
                
                {filteredChannels.length > 0 && (
                  <Button
                    variant="tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateChannelsClick(assistant);
                    }}
                  >
                    <FaTools /> {t('aiAssistant.manageChannels')}
                  </Button>
                )}
                
                {user && user.role !== 'experience' && availableBrands.length > 0 && (
                  <Button
                    variant="tertiary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssociateBrandClick(assistant._id);
                    }}
                  >
                    <FaTag /> {assistant.associated_brand_id ? t('aiAssistant.changeBrand') : t('aiAssistant.associateBrand')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="connections-container">
        <h4 className="connections-heading">{t('aiAssistant.connections')}</h4>
        <div className="circles-wrapper">
          {validEmails.map((email, index) => (
            <div key={`email-${index}`} className="connection-circle email-circle" data-email={email}>
              <FaEnvelope />
              <span className="circle-text">{email}</span>
            </div>
          ))}
          
          {filteredChannels.map((channel, index) => (
            <div key={`channel-${index}`} className="connection-circle ai-channel-circle" data-channel={channel}>
              <div className="channel-icon">
                {getChannelIcon(channel)}
              </div>
              <span className="circle-text">{channel}</span>
            </div>
          ))}
          
          {user && user.role !== 'experience' && availableBrands.map((brand, index) => (
            <div key={`brand-${index}`} className="connection-circle brand-circle" data-brand={brand.id}>
              <FaTag />
              <span className="circle-text">{brand.name}</span>
            </div>
          ))}
        </div>
      </div>
      
      {showEmailSelector && (
        <div className="email-selector-modal">
          <div className="email-selector-content">
            <h3>{t('aiAssistant.selectEmail')}</h3>
            
            {associationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.emailAssociatedSuccess')}
              </div>
            )}
            
            {associationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.emailAssociatedError')}
              </div>
            )}
            
            {associationSuccess === null && (
              <>
                <div className="email-options">
                  {validEmails.map((email, index) => (
                    <Button 
                      key={index}
                      variant="tertiary"
                      onClick={() => associateEmailWithAssistant(email)}
                      disabled={associationLoading}
                    >
                      {email}
                    </Button>
                  ))}
                  
                  {aiAssistants.find(a => a._id === selectedAssistantForEmail)?.associated_email && (
                    <Button 
                      variant="tertiary"
                      onClick={() => {
                        if (window.confirm(t('aiAssistant.confirmRemoveEmail'))) {
                          const assistant = aiAssistants.find(a => a._id === selectedAssistantForEmail);
                          removeEmailAssociation(selectedAssistantForEmail, assistant.associated_email);
                        }
                      }}
                      disabled={associationLoading}
                    >
                      <FaTimes /> {t('aiAssistant.removeEmail')}
                    </Button>
                  )}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowEmailSelector(false);
                      setSelectedAssistantForEmail(null);
                    }}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {showChannelSelector && (
        <div className="channel-selector-modal">
          <div className="channel-selector-content">
            <h3>{t('aiAssistant.manageChannels')}</h3>
            
            {channelAssociationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.channelsAssociatedSuccess')}
              </div>
            )}
            
            {channelAssociationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.channelsAssociatedError')}
              </div>
            )}
            
            {channelAssociationSuccess === null && (
              <>
                <div className="channel-options">
                  {Object.entries(availableChannels)
                    .filter(([channelName]) => channelName !== 'test')
                    .map(([channelName, isAvailable]) => (
                      isAvailable && (
                        <div key={channelName} className="channel-option">
                          <label className="channel-toggle">
                            <input
                              type="checkbox"
                              checked={selectedChannels[channelName] || false}
                              onChange={() => handleChannelToggle(channelName)}
                              disabled={channelAssociationLoading}
                            />
                            <span className="channel-toggle-slider"></span>
                          </label>
                          <span className="channel-name">{channelName}</span>
                        </div>
                      )
                    ))}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="primary"
                    onClick={associateChannelsWithAssistant}
                    disabled={channelAssociationLoading}
                  >
                    {channelAssociationLoading ? (
                      <span className="spinner-icon"></span>
                    ) : (
                      t('aiAssistant.saveChannels')
                    )}
                  </Button>
                  
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowChannelSelector(false);
                      setSelectedAssistantForChannels(null);
                    }}
                    disabled={channelAssociationLoading}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showBrandSelector && (
        <div className="brand-selector-modal">
          <div className="brand-selector-content">
            <h3>{t('aiAssistant.selectBrand')}</h3>
            
            {brandAssociationSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.brandAssociatedSuccess')}
              </div>
            )}
            
            {brandAssociationSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.brandAssociatedError')}
              </div>
            )}
            
            {brandAssociationSuccess === null && (
              <>
                <div className="brand-options">
                  {availableBrands.map((brand) => (
                    <Button 
                      key={brand.id}
                      variant="tertiary"
                      onClick={() => associateBrandWithAssistant(brand.id)}
                      disabled={brandAssociationLoading}
                      className="brand-option-button"
                    >
                      <FaTag /> {brand.name}
                      {brand.description && (
                        <span className="brand-description">{brand.description}</span>
                      )}
                    </Button>
                  ))}
                  
                  {aiAssistants.find(a => a._id === selectedAssistantForBrand)?.associated_brand_id && (
                    <Button 
                      variant="tertiary"
                      onClick={() => {
                        if (window.confirm(t('aiAssistant.confirmRemoveBrand'))) {
                          const assistant = aiAssistants.find(a => a._id === selectedAssistantForBrand);
                          removeBrandAssociation(selectedAssistantForBrand, assistant.associated_brand_id);
                        }
                      }}
                      disabled={brandAssociationLoading}
                    >
                      <FaTimes /> {t('aiAssistant.removeBrand')}
                    </Button>
                  )}
                </div>
                
                <div className="modal-actions">
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowBrandSelector(false);
                      setSelectedAssistantForBrand(null);
                    }}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <div className="ai-agent-modal">
          <div className="ai-agent-modal-content">
            <h3>{t('aiAssistant.createAssistant')}</h3>
            
            {createSuccess === true && (
              <div className="association-success">
                <FaCheck /> {t('aiAssistant.createSuccessDemo')}
              </div>
            )}
            
            {createSuccess === false && (
              <div className="association-error">
                <FaTimes /> {t('aiAssistant.createError')}
              </div>
            )}
            
            {createSuccess === null && (
              <>
                <div className="form-group">
                  <label htmlFor="newAgentName">{t('aiAssistant.name')}:</label>
                  <input
                    type="text"
                    id="newAgentName"
                    value={newAgentName}
                    onChange={(e) => setNewAgentName(e.target.value)}
                    className="ai-agent-input"
                    disabled={createLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newAgentPrompt">{t('aiAssistant.prompt')}:</label>
                  <textarea
                    id="newAgentPrompt"
                    value={newAgentPrompt}
                    onChange={(e) => setNewAgentPrompt(e.target.value)}
                    rows="4"
                    className="ai-agent-textarea"
                    disabled={createLoading}
                  />
                </div>
                
                <div className="ai-agent-tools-section">
                  <h4>{t('aiAssistant.selectTools')}</h4>
                  <div className="ai-agent-tools-grid">
                    {availableTools.map((tool) => (
                      <div key={tool.id} className="ai-agent-tool-item">
                        <input
                          type="checkbox"
                          id={`create-tool-${tool.id}`}
                          checked={selectedToolIds.includes(tool.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedToolIds(prev => [...prev, tool.id]);
                            } else {
                              setSelectedToolIds(prev => prev.filter(id => id !== tool.id));
                            }
                          }}
                          disabled={createLoading}
                        />
                        <label htmlFor={`create-tool-${tool.id}`}>
                          {getToolTranslation(tool.name)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <Button
                    variant="primary"
                    onClick={handleCreateAIAssistant}
                    disabled={createLoading}
                  >
                    {createLoading ? (
                      <span className="spinner-icon"></span>
                    ) : (
                      <>
                        <FaPlus /> {t('aiAssistant.create')}
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="secondary"
                    onClick={() => {
                      resetCreateForm();
                      setShowCreateAgentModal(false);
                    }}
                    disabled={createLoading}
                  >
                    {t('aiAssistant.cancel')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentsList;